import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { LocationsService } from '../api/services/LocationsService';

export type DashboardScope = 'global' | number;

export interface DashboardLocation {
  id: number;
  name: string;
  type: 'WAREHOUSE' | 'BRANCH';
}

interface DashboardLocationContextValue {
  scope: DashboardScope;
  setScope: (scope: DashboardScope) => void;
  locations: DashboardLocation[];
  locationsLoading: boolean;
  warehouseLocations: DashboardLocation[];
  branchLocations: DashboardLocation[];
}

const DashboardLocationContext = createContext<DashboardLocationContextValue | null>(null);

export function useDashboardLocation() {
  const ctx = useContext(DashboardLocationContext);
  if (!ctx) {
    throw new Error('useDashboardLocation must be used within DashboardLocationProvider');
  }
  return ctx;
}

interface DashboardLocationProviderProps {
  children: React.ReactNode;
}

export function DashboardLocationProvider({ children }: DashboardLocationProviderProps) {
  const [scope, setScopeState] = useState<DashboardScope>('global');
  const [locations, setLocations] = useState<DashboardLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  const setScope = useCallback((newScope: DashboardScope) => {
    setScopeState(newScope);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLocationsLoading(true);
    LocationsService.locationsControllerFindAll()
      .then((response: any) => {
        if (cancelled) return;
        const raw = Array.isArray(response) ? response : (response?.data ?? []);
        const mapped: DashboardLocation[] = raw
          .filter((l: any) => l?.id != null && l?.name && l?.type)
          .map((l: any) => ({
            id: Number(l.id),
            name: String(l.name),
            type: l.type === 'BRANCH' ? 'BRANCH' : 'WAREHOUSE',
          }));
        setLocations(mapped);
      })
      .catch(() => {
        if (!cancelled) setLocations([]);
      })
      .finally(() => {
        if (!cancelled) setLocationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const warehouseLocations = locations.filter((l) => l.type === 'WAREHOUSE');
  const branchLocations = locations.filter((l) => l.type === 'BRANCH');

  const value: DashboardLocationContextValue = {
    scope,
    setScope,
    locations,
    locationsLoading,
    warehouseLocations,
    branchLocations,
  };

  return (
    <DashboardLocationContext.Provider value={value}>{children}</DashboardLocationContext.Provider>
  );
}
